import "../styles/family-picker.scss";
import React, { useRef, useEffect, useCallback, memo } from "react";
import { GuiCheckbox } from "./global-ui/gui-checkbox";
import { SearchBox } from "./search-box";
import { GuiButton } from "./global-ui/gui-button";
import { Scroller } from "./scroller";
import { useSelector } from "react-redux";
import { pluralize, useUpdateState } from "../utilities/utilities";
import actions from "../actions/actions";

function MemberPickerComponent({ show, className, selected, onDone, onBack }) {
  let isMobile = useSelector((store) => store.page.isMobile);
  let loadedItemsRef = useRef();
  let [state, updateState] = useUpdateState(() => {
    return {
      search: "",
      filters: [],
      filterChanged: false,
      gridReset: 0,
    };
  });

  const checkedRef = useRef([]);
  useEffect(() => {
    checkedRef.current = Array.isArray(state.checked) ? state.checked : [];
  }, [state.checked]);

  useEffect(() => {
    if (show && loadedItemsRef.current) {
      // Reset selection when dialog opens or selection changes
      loadedItemsRef.current.forEach(
        (g) => (g.selected = selected.some((sel) => sel.id === g.id)),
      );
      updateState({ checked: [...selected] });
    }
  }, [show, selected]);

  // Debounced search handler to limit reload churn while typing
  const searchTimeoutRef = useRef();
  const onSearchChange = useCallback(
    (search) => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        updateState({
          search,
          loading: true,
          errorMessage: null,
          gridReset: state.gridReset + 1,
        });
      }, 300);
    },
    [state.gridReset, updateState],
  );

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const itemsNeeded = useCallback(
    async (page, pageSize, skipTotal) => {
      const currentPage = page || 1;
      const currentPageSize = pageSize || 50;

      const result = await actions.members.load(
        state.search || "",
        currentPage,
        currentPageSize,
        skipTotal === true,
      );

      if (result?.success === false) {
        updateState({
          loading: false,
          errorMessage: result?.errorMessage || "Unable to load members.",
        });
        return { list: [], totalCount: state?.totalCount ?? 0 };
      }

      if (result && Array.isArray(result.list)) {
        const checked = checkedRef.current || [];
        result.list.forEach(
          (g) => (g.selected = checked.some((sel) => sel.id === g.id)),
        );
      }

      updateState({
        loading: false,
        totalCount:
          skipTotal === true
            ? (state?.totalCount ?? 0)
            : (result?.totalCount ?? state?.totalCount ?? 0),
      });
      return result || { list: [], totalCount: state?.totalCount ?? 0 };
    },
    [state.search, state?.totalCount, updateState],
  );

  const onItemChange = useCallback(
    (id) => {
      let item = loadedItemsRef.current.find((i) => i.id === id);
      if (!item) return;
      item.selected = !item.selected;

      let checked = Array.isArray(state.checked) ? [...state.checked] : [];

      if (item.selected) checked = [...checked, item];
      else checked = checked.filter((g) => g.id !== id);

      updateState({ checked });
    },
    [state.checked, updateState],
  );

  const onItemClick = useCallback(
    (e) => {
      if (e.target.tagName === "DIV") {
        // Otherwise the checkbox's onChange will handle toggling it
        let id = e.currentTarget.getAttribute("data-id");
        onItemChange(parseInt(id, 10));
      }
    },
    [onItemChange],
  );

  const onDoneClick = useCallback(
    () => onDone(state.checked),
    [onDone, state.checked],
  );

  const renderItem = useCallback(
    (item) => (
      <div
        className={
          "item" +
          (item.selected ? " selected" : "") +
          (item.id === -1 ? " all-users" : "")
        }
        onClick={onItemClick}
        data-id={item.id}
        key={item.id}
      >
        <GuiCheckbox
          label={item.name}
          name={item.id}
          value={item.selected}
          onChange={onItemChange}
        />
        {/* We don't need to show user count for members as there is no column for it from parishSoft */}
        {/* <div className="count">{item.userCount}</div> */}
      </div>
    ),
    [onItemChange, onItemClick],
  );

  return (
    <div
      className={
        "family-picker " + (show ? " open " : " closed ") + (className || "")
      }
    >
      <div className="top-area">
        <div id="back" className="back" onClick={onBack}>
          <i className="far fa-chevron-left"></i>
          {isMobile ? "" : " Back"}
        </div>
        <SearchBox onSearchChange={onSearchChange} searching={state.loading} />
        {state?.errorMessage && (
          <div
            className="error-banner"
            role="alert"
            style={{ color: "#b00020", marginTop: 8 }}
          >
            {state.errorMessage}
          </div>
        )}

        {/* Uncomment in future for filters and done button */}
        {/* <div className="my-alerts-mobile-filter">
          <FilterDropdown
            filters={filterOptions}
            onFilterChange={onFilterChange}
          />
        </div> */}

        <GuiButton dataQa="done" onClick={onDoneClick}>
          Done
        </GuiButton>
      </div>

      <Scroller
        reset={state.gridReset}
        itemHeight={47}
        onItemsNeeded={itemsNeeded}
        loadedItemsRef={loadedItemsRef}
        itemTemplate={renderItem}
      />

      <div className="footer">
        {state?.totalCount} {pluralize("Member", state?.totalCount)}
        <span> | {state.checked?.length} Selected</span>
      </div>
    </div>
  );
}

function propsAreEqual(prevProps, nextProps) {
  if (prevProps.show !== nextProps.show) return false;
  if (prevProps.className !== nextProps.className) return false;
  if (prevProps.onDone !== nextProps.onDone) return false;
  if (prevProps.onBack !== nextProps.onBack) return false;
  const prevIds = (prevProps.selected || [])
    .map((i) => i.id)
    .sort()
    .join(",");
  const nextIds = (nextProps.selected || [])
    .map((i) => i.id)
    .sort()
    .join(",");
  return prevIds === nextIds;
}

export const MemberPicker = memo(MemberPickerComponent, propsAreEqual);
